import { ReactNode } from "react";

import { SortOrder } from "@bridgesplit/utils";
import { DispatchType, FormInputType } from "@bridgesplit/react";

export type TableKey = string | number;

export type TableRow<T> = {
    data: T;
    key: TableKey;
};

export type SortBy<T> = (params: TableRow<T>) => TableKey | undefined;

type TableAlignment = "left" | "center" | "right";

export type TableColumn<T> = {
    dataIndex: string;
    title: string | JSX.Element;
    align?: TableAlignment;
    width?: string;
    tooltip?: string;
    render: (props: TableRow<T>, index: number) => ReactNode;
    sortBy?: SortBy<T>;
    /** Comparator strategy for `sortBy`'s return type. Default: "numeric". */
    sortType?: "numeric" | "text";
    apiSortKey?: string;
};

/** Predicate filter shown as a chip above the table. `predicate: null` = filter disabled. */
export interface DataGridFilter<T> {
    component: ReactNode;
    predicate: ((row: TableRow<T>) => boolean) | null;
}

export const TABLE_CTA_COLUMN_KEY = "cta";
export const TABLE_ROW_HEIGHT = 60;
export const TABLE_ROW_HEIGHT_PX = `${TABLE_ROW_HEIGHT}px`;

export const TABLE_BUFFER_HEIGHT = 100;

export interface PaginationProps {
    type: "api" | "managed";
    defaultSortKey?: string;
    defaultOrder?: SortOrder;
    pageSize?: number;
}

// `T` is unused but kept for type-system symmetry with `TableProps<T>` so callers
// can spell `paginationProps={ ... } satisfies ManagedPaginationProps<MyRowType>`.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface ManagedPaginationProps<T> extends PaginationProps {
    type: "managed";
}

export interface ApiPaginationProps extends PaginationProps {
    type: "api";
    totalDataCount: number | undefined;
    controller: PaginationController;
}

export interface PaginationController {
    page: number;
    setPage: DispatchType<number>;
    resetPage: () => void;
    orderBy: string;
    setOrderBy?: FormInputType<string>;
    order: SortOrder;
    setOrder?: FormInputType<SortOrder>;
    totalDataCount?: number;
}
